import { FormControl, FormControlLabel, FormGroup, FormHelperText, Box, Typography } from '@mui/material';
import {
  CheckboxWithViewOnly,
  FormFieldWrapper,
  I18nOptionsAutocompleteFieldWithController,
  NumberField,
  NumberFieldWithController,
} from '@procureai/ui-form-mui';
import { FormBaseProps } from '@procureai/ui-modules-mui';
import { PriorityList } from '@procureai/ui-mui';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AwardingConfigurationFormData } from '../../AwardingConfigurationWidget.schema';
import { TsrctDemandAwardingRuleVariant } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';

const name = 'rules' as const;
const awardingRuleOptions = Object.values(TsrctDemandAwardingRuleVariant);

// Local copy of the shared AwardingConfigForm, offering tesseract's rule variants instead of the
// shared config's (BUG-3658). It reuses the shared form's labels.
export const AwardingConfigurationForm = ({ viewOnly }: FormBaseProps) => {
  const { t } = useTranslation(I18nNamespace.modules, { keyPrefix: 'AwardingConfigForm' });

  const { control, watch } = useFormContext<AwardingConfigurationFormData>();
  const { fields, move, prepend, remove } = useFieldArray<AwardingConfigurationFormData>({
    name,
    control,
    shouldUnregister: true,
  });

  const rulesWatch = watch(name);
  const dayWatch = watch('deadline.days');
  const selectedVariants = rulesWatch?.map((rule) => rule.variant) ?? [];
  const nonSelectedOptions = awardingRuleOptions.filter((option) => !selectedVariants.includes(option));

  return (
    <>
      <FormFieldWrapper label={t('rules.label')} required>
        <PriorityList
          items={fields}
          onMove={move}
          viewOnly={viewOnly}
          onAddClick={nonSelectedOptions.length === 0 ? undefined : () => prepend({ variant: nonSelectedOptions[0] })}
          onDeleteClick={remove}
          renderItem={({ item, index, locked }) => {
            const currentVariant = rulesWatch[index]?.variant;

            // Make all not selected options available plus the currently selected one
            const availableOptions = awardingRuleOptions.filter(
              (option) => option === currentVariant || nonSelectedOptions.includes(option),
            );

            return (
              <Box sx={{ flex: 1 }}>
                <I18nOptionsAutocompleteFieldWithController
                  viewOnly={viewOnly}
                  control={control}
                  key={item.id}
                  name={`${name}.${index}.variant`}
                  i18n={{ ns: I18nNamespace.common, keyPrefix: 'AwardingRuleVariant' }}
                  options={availableOptions}
                  size="small"
                  disabled={locked}
                  sx={viewOnly ? { marginTop: 2 } : undefined}
                />
              </Box>
            );
          }}
        />
      </FormFieldWrapper>

      <NumberFieldWithController
        viewOnly={viewOnly}
        viewOnlyFormat={(formattedValue) => t('deadline.days.viewOnly', { value: formattedValue, count: dayWatch })}
        name="deadline.days"
        control={control}
        label={t('deadline.days.label')}
        infoText={t('deadline.days.info')}
        required
        endAdornment={
          <Typography variant="body1" color="text.secondary">
            {t('deadline.days.unit')}
          </Typography>
        }
        helperText={t('deadline.days.helper')}
      />

      <Typography variant="subtitle1" sx={{ color: 'text.primary', pt: 3 }}>
        {t('automation.subtitle')}
      </Typography>

      <Controller
        control={control}
        name="automation.enabled"
        render={({ field }) => (
          <FormControl variant="standard">
            <FormGroup>
              <FormControlLabel
                label={t('automation.enabled.label')}
                labelPlacement="start"
                control={
                  <CheckboxWithViewOnly
                    viewOnly={viewOnly}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
              />
            </FormGroup>
            <FormHelperText>{t('automation.enabled.helper')}</FormHelperText>
          </FormControl>
        )}
      />

      {!viewOnly && (
        <>
          <NumberField label={t('automation.sourceCaseCountMin.label')} disabled value={5} />
          <NumberField label={t('automation.bidCountMin.label')} disabled value={1} />
        </>
      )}
    </>
  );
};
